// PastCanvasSeen — 「過去キャンバス」ボタンの「New!」バッジを「初回だけ」出すための
// seen フラグ永続化 (infrastructure 層).
//
// 背景 (past-canvas-new-document-2026-07-05.md §7 Slice 2 / consult 2026-07-09):
//   - 過去キャンバス一覧の入口ボタンを **全員に見せる**が、新機能訴求として
//     「New!」バッジを出す。常時 New! だと陳腐化するので **初回に入口を押したら
//     消す** (以後は非表示)。押下でオーナー分岐 (一覧モーダル / ログイン誘導) する
//     どちらの経路でも「見た」扱いにする。
//   - 絵ログ共有の `timelapseShareSeen.ts` を**そっくり複製**し、別 localStorage
//     key で独立管理する (= 絵ログの New! と過去キャンバスの New! は別々に消える)。
//   - Classic / Modern 両入口で同じ store を参照するので、どちらで押しても両方の
//     バッジが消える。
//
// 設計 (`timelapseShareSeen.ts` と同流儀):
//   - boolean を文字列 `'1'` で保存 (= 在れば seen 済)。不在 / storage 不可は「未 seen」
//     (= まだ出す) 扱い。例外を throw しない。
//   - StorageLike 抽象 + sentinel probe。test で storage mock 注入可能。
//
// Tests: pastCanvasSeen.test.ts (storage-injection style)。

/** localStorage キー: 過去キャンバスの New! バッジを消したか (= 初回に入口を押したか). */
export const PAST_CANVAS_SEEN_KEY = 'oekaki:past-canvas-seen';

/** Minimal Storage shape this module uses (timelapseShareSeen.ts と同流儀). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PastCanvasSeenStore {
  /** 既に New! バッジを消したか (= 初回に入口を押したか). 不在 / storage 不可 → false. */
  hasSeen(): boolean;
  /** 初回に入口を押したことを記録 (= 以後バッジ非表示). storage 不可なら no-op. */
  markSeen(): void;
  /** key を削除. テスト / リセット用. storage 不可なら no-op. */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:past-canvas-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreatePastCanvasSeenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= hasSeen always false, markSeen silently drops).
   */
  storage?: StorageLike | null;
}

/**
 * `PastCanvasSeenStore` のインスタンスを構築. storage 不可でも例外を出さず、
 * 「未 seen」扱い (= 出すが永続化されないだけ) で動作を継続する.
 */
export const createPastCanvasSeenStore = (
  opts: CreatePastCanvasSeenStoreOptions = {},
): PastCanvasSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(PAST_CANVAS_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(PAST_CANVAS_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(PAST_CANVAS_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
